"use client";
import { useState } from "react";
import { contactSales } from "~/lib/network/billing";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactSalesModal({
  isOpen,
  onClose,
}: ContactSalesModalProps) {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await contactSales({
        email: formData.email,
        name: formData.name || undefined,
        company: formData.company || undefined,
        message: formData.message || undefined,
      });

      setSuccess(true);
      // Reset form
      setFormData({
        email: "",
        name: "",
        company: "",
        message: "",
      });

      // Auto-close after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit contact request"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <dialog className="modal" open={isOpen}>
      <div className="modal-box bg-base-200">
        <button
          type="button"
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          onClick={onClose}
        >
          ✕
        </button>

        <h3 className="font-bold text-lg mb-4">Contact Sales</h3>

        {success ? (
          <div className="alert alert-success">
            <IoCheckmarkCircle className="w-6 h-6" />
            <span>Request submitted successfully!</span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 max-w-md mx-auto p-4"
          >
            {error && (
              <div className="alert alert-error">
                <IoCloseCircle className="w-6 h-6" />
                <span>{error}</span>
              </div>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                Email <span className="text-error">*</span>
              </legend>
              <input
                type="email"
                name="email"
                className="input w-full"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Name</legend>
              <input
                type="text"
                name="name"
                className="input w-full"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Company</legend>
              <input
                type="text"
                name="company"
                className="input w-full"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Message</legend>
              <textarea
                name="message"
                className="textarea h-24 w-full"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your needs..."
              />
            </fieldset>

            <div className="modal-action justify-center">
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading || !formData.email}
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
      <form method="dialog" className="modal-backdrop" onClick={onClose}>
        <button type="button">close</button>
      </form>
    </dialog>
  );
}
